import { ProductsSubUsageType } from '../types'

import type { $Enums } from '@prisma/client'

import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { api } from '@/utils/api'
import { IS_DEV } from '@/helpers/env'


import { useMemo, useEffect } from 'react';
export function useSubUsage({
  appName,
  enabled,
}: {
  appName: $Enums.SubLogAppName
  enabled?: boolean
}) {
  const sessionInfo = useSessionInfo()
  const { userId, isSignIn } = sessionInfo

  /* 包含匿名登录和正常登录 根据需求随意切换 */
  // const isAuth = sessionInfo.sessionAuthenticated
  /* 只包含正常登录 */
  const isAuth = sessionInfo.isSignIn

  const _enabled = useMemo(() => {
    if (enabled !== undefined) {
      return !!(enabled && isAuth && appName)
    }
    return !!(isAuth && appName)
  }, [enabled, isAuth, appName])

  const {
    data,
    isInitialLoading,
    refetch: _refetch,
    isFetched,
    isFetching,
    remove,
  } = api.subUsage.getSubUsage.useQuery(
    {
      appName,
    },
    {
      enabled: _enabled,
      onError: async (error) => {
        if (IS_DEV) {
          // eslint-disable-next-line no-console
          console.error('useSubUsage', error)
        }
      },
      onSuccess: (data) => {},
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  )

  const refetch: typeof _refetch = (...params) => {
    if (!_enabled) {
      if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('isAuth is false')
      }
      return Promise.resolve({
        usageList: [],
      }) as any
    }
    return _refetch(...params)
  }

  const apiCtx = api.useUtils()
  useEffect(() => {
    // 登录之后刷新额度
    if (_enabled) {
      apiCtx.subUsage.getSubUsage.invalidate()
    }
  }, [_enabled, userId])

  const usageList = useMemo(() => {
    if (!data) {
      return []
    }
    return data.usageList
  }, [data])

  const creditsUsage = useMemo(() => {
    let mergedCreditsUsage: (typeof usageList)[number] | undefined

    for (const item of usageList) {
      if (item.usageType !== ProductsSubUsageType.credits) {
        continue
      }

      if (!mergedCreditsUsage) {
        mergedCreditsUsage = { ...item }
        continue
      }

      mergedCreditsUsage = {
        ...mergedCreditsUsage,
        totalCount: mergedCreditsUsage.totalCount + item.totalCount,
        useCount: mergedCreditsUsage.useCount + item.useCount,
      }
    }

    return mergedCreditsUsage
  }, [usageList])

  const remainingUsageMap = useMemo(() => {
    const obj: Record<string, number> = {}
    usageList.forEach((item) => {
      const key = `${item.appName}_${item.usageType}`
      const remaining = item.totalCount - item.useCount
      obj[key] = (obj[key] || 0) + remaining
    })
    return obj
  }, [usageList])

  const countInfo = useMemo(() => {
    const creditsUseCount = creditsUsage?.useCount || 0
    const creditsTotalCount = creditsUsage?.totalCount || 0
    return {
      creditsUseCount,
      creditsTotalCount,
    }
  }, [creditsUsage])

  return {
    usageList,
    loading: isInitialLoading,
    refetch,
    isFetched,
    isFetching,
    remainingUsageMap,
    /** credits 相关额度 */
    creditsUsage,
    countInfo,
    remove,
  }
}

export type TUseSubUsage = ReturnType<typeof useSubUsage>
